import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { GripVertical, ChevronUp } from "lucide-react";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { SearchablePicker } from "@/shared/components/SearchablePicker";
import type { Entry, Matter } from "@/shared/contexts/DashboardDataContext";

// App icon imports (same set as EntriesTable)
import wordIcon from "../../../assets/applications/WordIcon.svg";
import adobeIcon from "../../../assets/applications/AdobeIcon.svg";
import outlookIcon from "../../../assets/applications/OutlookIcon.svg";
import powerpointIcon from "../../../assets/applications/PowerpointIcon.svg";
import excelIcon from "../../../assets/applications/ExcelIcon.svg";
import zoomIcon from "../../../assets/applications/ZoomIcon.svg";
import chromeIcon from "../../../assets/applications/ChromeIcon.svg";
import teamsIcon from "../../../assets/applications/TeamsIcon.svg";
import calendarIcon from "../../../assets/applications/CalendarIcon.svg";
import kofaxIcon from "../../../assets/applications/KofaxIcon.svg";
import foxitIcon from "../../../assets/applications/FoxitIcon.svg";
import photosIcon from "../../../assets/applications/WindowsPhotos.svg";
import combineIconImg from "../../../assets/applications/CombineIcon.svg";
import combineIconWhite from "../../../assets/applications/CombineIconWhite.svg";
import duplicateIcon from "../../../assets/applications/DuplicateIcon.svg";
import duplicateIconWhite from "../../../assets/applications/DuplicateIconWhite.svg";
import phoneIcon from "../../../assets/icons/phone.svg";
import phoneWhiteIcon from "../../../assets/icons/phoneWhite.svg";
import userInputIcon from "../../../assets/icons/userInput.svg";
import userInputWhiteIcon from "../../../assets/icons/userInputWhite.svg";

interface CombineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: Entry[];
  matters: Matter[];
  onConfirm: (ids: number[], matterId: string, mainEntryId: number) => void;
  onCancel: () => void;
}

function useAppIcon(darkMode: boolean) {
  return (application?: string) => {
    switch (application) {
      case "Microsoft Word": return wordIcon;
      case "Microsoft Outlook": return outlookIcon;
      case "Microsoft Outlook Calendar": return calendarIcon;
      case "Adobe Acrobat": return adobeIcon;
      case "Microsoft Powerpoint": return powerpointIcon;
      case "Microsoft Excel": return excelIcon;
      case "Zoom": return zoomIcon;
      case "Google Chrome": return chromeIcon;
      case "Microsoft Teams": return teamsIcon;
      case "Phone": return darkMode ? phoneWhiteIcon : phoneIcon;
      case "User Input": return darkMode ? userInputWhiteIcon : userInputIcon;
      case "Kofax": return kofaxIcon;
      case "Foxit": return foxitIcon;
      case "Combine": return darkMode ? combineIconWhite : combineIconImg;
      case "Duplicate": return darkMode ? duplicateIconWhite : duplicateIcon;
      case "Photos": return photosIcon;
      default: return null;
    }
  };
}

// Sortable entry row component
function SortableEntryRow({
  entry,
  isMain,
  onPromote,
  darkMode,
  getIcon,
}: {
  entry: Entry;
  isMain: boolean;
  onPromote: () => void;
  darkMode: boolean;
  getIcon: (app?: string) => string | null;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
    borderBottom: "1px solid hsl(var(--border) / 0.3)",
    ...(isMain
      ? {
          borderLeft: "3px solid hsl(var(--brand))",
          backgroundColor: darkMode
            ? "hsl(var(--card))"
            : "hsl(var(--secondary))",
        }
      : { borderLeft: "3px solid transparent" }),
  };

  const icon = getIcon(entry.application);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center gap-3 px-4 py-2.5 text-[13px] min-h-[48px] cursor-default touch-none select-none"
    >
      {/* Left slot — "Main" label or promote arrow, fixed width for alignment */}
      <div className="flex-shrink-0 w-10 flex items-center justify-center">
        {isMain ? (
          <span
            className="text-[10px] font-semibold rounded px-1 py-0.5"
            style={{
              color: darkMode
                ? "hsl(var(--accent-blue))"
                : "hsl(var(--brand-alt))",
              backgroundColor: darkMode
                ? "hsl(var(--accent-blue) / 0.15)"
                : "hsl(var(--brand-alt) / 0.1)",
            }}
          >
            Main
          </span>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPromote();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className={`p-0.5 rounded transition-colors ${
              darkMode
                ? "hover:bg-white/10 text-gray-500 hover:text-gray-300"
                : "hover:bg-gray-200 text-gray-400 hover:text-gray-600"
            }`}
            title="Set as main entry"
          >
            <ChevronUp size={14} />
          </button>
        )}
      </div>

      {/* Drag grip indicator (visual only) */}
      <GripVertical
        size={14}
        className={`flex-shrink-0 ${darkMode ? "text-gray-600" : "text-gray-300"}`}
      />

      {/* App icon */}
      <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
        {icon && <img src={icon} alt="" className="w-5 h-5" />}
      </div>

      {/* Narrative */}
      <div className="flex-1 min-w-0 mr-3">
        <span className="whitespace-normal break-words leading-snug">
          {entry.description}
        </span>
      </div>

      {/* Units badge — matches EntriesTable .unitsBadge */}
      <div
        className="flex-shrink-0 text-[13px] px-2.5 py-1 min-w-[50px] text-center"
        style={{
          fontWeight: 525,
          borderRadius: "var(--radius)",
          backgroundColor: darkMode
            ? "hsl(var(--card))"
            : "hsl(var(--brand))",
          color: darkMode
            ? "hsl(var(--foreground))"
            : "hsl(var(--background))",
          border: darkMode
            ? "none"
            : "1px solid hsl(var(--background))",
        }}
      >
          {entry.duration.toFixed(2).endsWith("0")
            ? entry.duration.toFixed(1)
            : entry.duration.toFixed(2)}
      </div>
    </div>
  );
}

export const CombineDialog: React.FC<CombineDialogProps> = ({
  open,
  onOpenChange,
  entries,
  matters,
  onConfirm,
  onCancel,
}) => {
  const { darkMode } = useDarkMode();
  const getIcon = useAppIcon(darkMode);
  // Ordered entry IDs — first one is the main entry
  const [orderedIds, setOrderedIds] = useState<number[]>([]);
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Initialize order and matter when dialog opens
  useEffect(() => {
    if (!open) return;

    // Only initialise when the dialog first opens (orderedIds is empty)
    if (orderedIds.length > 0) return;

    setOrderedIds(entries.map((e) => e.id));
    setSearchTerm("");

    // Auto-fill matter only when all entries share the same one
    const entryMatterIds = entries.map((e) => e.matter_id).filter(Boolean) as string[];
    const uniqueMatters = Array.from(new Set(entryMatterIds));
    if (uniqueMatters.length === 1) {
      setSelectedMatterId(uniqueMatters[0]);
    } else {
      setSelectedMatterId(null);
    }
  }, [open, entries]);

  const mainEntryId = orderedIds[0] ?? null;

  // Determine if matter needs manual selection
  const matterIds = useMemo(
    () => Array.from(new Set(entries.map((e) => e.matter_id).filter(Boolean) as string[])),
    [entries]
  );
  const noMatters = matterIds.length === 0;

  // Combined units total
  const combinedTotal = useMemo(
    () => entries.reduce((sum, e) => sum + (e.duration || 0), 0),
    [entries]
  );

  // Matter source: use API matters if available, otherwise derive from the entries
  const sourceMattersList = useMemo((): Matter[] => {
    if (matters.length > 0) return matters;
    const seen = new Set<string>();
    const result: Matter[] = [];
    for (const e of entries) {
      if (e.matter_id && !seen.has(e.matter_id)) {
        seen.add(e.matter_id);
        result.push({
          matter: e.matter || e.matter_name || "",
          matter_id: e.matter_id,
          client: e.client || "",
          description: "",
          task_group: "",
          activity_group: "",
        });
      }
    }
    return result;
  }, [matters, entries]);

  // Transform matters for SearchablePicker (same format as EditDialog)
  const availableMatters = useMemo(() => {
    return sourceMattersList.map((matter) => ({
      matter: matter.description,
      client: matter.client,
      name: matter.matter,
      matter_id: matter.matter_id,
      combined: `${matter.matter} | ${matter.description} - ${matter.client}`,
      combinedTrimmed: `${matter.matter.trim()} | ${matter.description.trim()} - ${matter.client.trim()}`,
    }));
  }, [sourceMattersList]);

  // Matter search filtering (same scoring as EditDialog)
  const filteredMatters = useMemo(() => {
    if (!searchTerm) return availableMatters;
    const query = searchTerm.toLowerCase();
    return availableMatters
      .map((matter) => {
        let score = 0;
        const matterName = matter.matter.toLowerCase();
        const name = matter.name.toLowerCase();
        const client = matter.client.toLowerCase();
        if (matterName === query || name === query) score += 100;
        else if (matterName.startsWith(query) || name.startsWith(query)) score += 50;
        else if (matterName.includes(query) || name.includes(query)) score += 25;
        if (client === query) score += 80;
        else if (client.startsWith(query)) score += 40;
        else if (client.includes(query)) score += 20;
        return { matter, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 20)
      .map((item) => item.matter);
  }, [searchTerm, availableMatters]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrderedIds((prev) => {
        const oldIndex = prev.indexOf(active.id as number);
        const newIndex = prev.indexOf(over.id as number);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const promoteToMain = (id: number) => {
    setOrderedIds((prev) => {
      const without = prev.filter((i) => i !== id);
      return [id, ...without];
    });
  };

  const handleConfirm = () => {
    if (!selectedMatterId || mainEntryId === null) return;
    onConfirm(
      orderedIds,
      selectedMatterId,
      mainEntryId
    );
  };

  // Build ordered entries from orderedIds
  const orderedEntries = useMemo(
    () => orderedIds.map((id) => entries.find((e) => e.id === id)).filter(Boolean) as Entry[],
    [orderedIds, entries]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Combine Entries</DialogTitle>
          <DialogDescription>
            Select a matter and drag to reorder. The top entry drives the combined narrative.
          </DialogDescription>
        </DialogHeader>

        {/* Matter section — on top, using same SearchablePicker as EditDialog */}
        <div className="flex flex-col w-full gap-1.5">
          {matterIds.length > 1 && (
            <div className="w-full bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800/50 rounded-md p-2.5">
              <p className="text-xs text-yellow-800 dark:text-yellow-400 font-medium">
                Selected entries have different matters. Choose one:
              </p>
            </div>
          )}
          {noMatters && (
            <div className="w-full bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800/50 rounded-md p-2.5">
              <p className="text-xs text-yellow-800 dark:text-yellow-400 font-medium">
                No matter assigned. Select a matter for the combined entry:
              </p>
            </div>
          )}
          <SearchablePicker
            items={filteredMatters}
            value={selectedMatterId}
            onChange={(matterId) => setSelectedMatterId(matterId)}
            getValue={(m) => m.matter_id}
            getLabel={(m) => m.combinedTrimmed}
            placeholder="Select matter..."
            searchPlaceholder="Search matter..."
            emptyMessage="No matter found."
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            popoverAlign="start"
            popoverWidth="var(--radix-popover-trigger-width)"
            error={!selectedMatterId}
          />
        </div>

        {/* Sortable entry list */}
        <div
          className="rounded-md overflow-y-auto"
          style={{ border: "1px solid hsl(var(--border))", maxHeight: "340px" }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedIds}
              strategy={verticalListSortingStrategy}
            >
              {orderedEntries.map((entry, index) => (
                <SortableEntryRow
                  key={entry.id}
                  entry={entry}
                  isMain={index === 0}
                  onPromote={() => promoteToMain(entry.id)}
                  darkMode={darkMode}
                  getIcon={getIcon}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        {/* Combined total + actions */}
        <div className={`border-t pt-3 ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
          <div className={`text-sm font-medium mb-3 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
            Combined total:{" "}
            <span className={darkMode ? "text-white" : "text-gray-900"}>
              {combinedTotal.toFixed(1)} hrs
            </span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedMatterId || mainEntryId === null}
              className={!selectedMatterId || mainEntryId === null ? "opacity-50 cursor-not-allowed" : ""}
            >
              Combine
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
